import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, TrendingUp, ArrowRight, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const navigate = useNavigate();
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    company_name: '',
    city: '',
    contact_number: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/categories`);
      if (response.data.categories && response.data.categories.length > 0) {
        setCategories(response.data.categories);
      } else {
        // Seed categories if empty
        await axios.post(`${BACKEND_URL}/api/seed-categories`);
        const retryResponse = await axios.get(`${BACKEND_URL}/api/categories`);
        setCategories(retryResponse.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Use fallback categories
      setCategories(fallbackCategories);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fallbackCategories = [
    {
      category_id: "cat-spices",
      name: "Spices & Seasonings",
      slug: "spices-seasonings",
      description: "Premium Indian spices and seasonings for global cuisines",
      image_url: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-pulses",
      name: "Pulses, Grains & Seeds",
      slug: "pulses-grains-seeds",
      description: "Quality pulses, grains and seeds from Indian farms",
      image_url: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-fruits",
      name: "Fresh Fruits & Vegetables",
      slug: "fresh-fruits-vegetables",
      description: "Farm-fresh fruits and vegetables from India",
      image_url: "https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-fmcg",
      name: "FMCG & Packaged Foods",
      slug: "fmcg-packaged-foods",
      description: "Ready-to-eat and packaged Indian food products",
      image_url: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-dryfruits",
      name: "Dry Fruits, Nuts & Dehydrated Foods",
      slug: "dry-fruits-nuts",
      description: "Premium dry fruits, nuts and dehydrated food products",
      image_url: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-oils",
      name: "Edible Oils, Sweeteners & Staples",
      slug: "edible-oils-sweeteners",
      description: "Quality edible oils, jaggery, sugar and food staples",
      image_url: "https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=600&h=400&fit=crop&q=80"
    },
    {
      category_id: "cat-hardware",
      name: "Hardware & Kitchenware",
      slug: "hardware-kitchenware",
      description: "Stainless steel utensils, cookware and hardware products",
      image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop&q=80"
    }
  ];

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        full_name: registerForm.company_name,
        email: `${registerForm.contact_number}@exporter.goldenport.in`,
        mobile: registerForm.contact_number,
        company_name: registerForm.company_name,
        message: `Exporter Registration from ${registerForm.city}. Company: ${registerForm.company_name}, Contact: ${registerForm.contact_number}`
      });
      toast.success('Registration submitted successfully! We will contact you soon.');
      setIsRegisterOpen(false);
      setRegisterForm({ company_name: '', city: '', contact_number: '' });
    } catch (error) {
      toast.error('Failed to submit registration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const problems = [
    {
      icon: Globe,
      title: 'International Buyer Access',
      description: 'We connect Indian exporters with suitable overseas buyers.'
    },
    {
      icon: TrendingUp,
      title: 'Trade Finance Coordination',
      description: 'We help businesses coordinate working capital and export finance solutions.'
    }
  ];

  const displayCategories = categories.length > 0 ? categories : fallbackCategories;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 44, 77, 0.8), rgba(11, 44, 77, 0.8)), url('https://images.unsplash.com/photo-1645301918921-6c744ab66e6f?w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Helping Indian Exporters Find<br />Global Buyers & Trade Finance
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            GoldenPort helps manufacturers and exporters with international market access, trade finance coordination, export documentation, and shipment support.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button 
              size="lg" 
              className="bg-[#0B2C4D] hover:bg-[#0a2542] text-white border-2 border-[#C9A24D] text-lg px-8 py-6"
              onClick={() => setIsRegisterOpen(true)}
              data-testid="register-exporter-btn"
            >
              Register As Exporter
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-transparent border-2 border-[#C9A24D] text-white hover:bg-[#C9A24D] hover:text-[#0B2C4D] text-lg px-8 py-6"
              onClick={() => navigate('/contact')}
              data-testid="contact-btn"
            >
              Talk to Our Team
            </Button>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-[#0B2C4D] mb-6">Who is GoldenPort?</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              GoldenPort International Pvt. Ltd. is an Indian export facilitation company.
              We help Indian manufacturers and MSMEs export their products safely to other countries.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We manage buyers, payments, paperwork, and shipment so exporters stay safe.
            </p>
          </div>
        </div>
      </section>

      {/* Problems We Solve Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-[#0B2C4D] text-center mb-12">What Problem We Solve</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {problems.map((problem, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-[#C9A24D]"
              >
                <problem.icon className="w-16 h-16 text-[#0B2C4D] mb-4" />
                <h3 className="text-2xl font-bold text-[#0B2C4D] mb-3">{problem.title}</h3>
                <p className="text-gray-700 mb-4">{problem.description}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#C9A24D] text-[#0B2C4D] hover:bg-[#C9A24D]"
                  onClick={() => navigate('/contact')}
                >
                  Learn More
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section 
        className="py-20 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.97), rgba(255, 255, 255, 0.97)), url('https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-[#0B2C4D] text-center mb-4">Our Products</h2>
          <p className="text-xl text-gray-600 text-center mb-4 max-w-3xl mx-auto">
            We export a wide range of premium Indian products across 7 major categories
          </p>
          <p className="text-sm text-gray-500 text-center mb-12 max-w-2xl mx-auto">
            Product availability is subject to sourcing and confirmation
          </p>
          
          {loadingCategories ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A24D] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading categories...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto mb-12">
                {displayCategories.slice(0, 7).map((category, index) => (
                  <div 
                    key={category.category_id || index}
                    className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group cursor-pointer border border-gray-100"
                    onClick={() => navigate(`/products?category=${category.slug || category.category_id}`)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0B2C4D]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-[#0B2C4D] mb-2 group-hover:text-[#C9A24D] transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {category.description}
                      </p>
                      <div className="flex items-center text-[#C9A24D] font-semibold text-sm group-hover:translate-x-2 transition-transform">
                        View Products <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="text-center">
                <Button 
                  size="lg"
                  className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold text-lg px-10 py-6"
                  onClick={() => navigate('/products')}
                  data-testid="view-all-products-btn"
                >
                  View All Products
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-[#0B2C4D] text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Export Safely?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            GoldenPort will manage everything for you.
          </p>
          <Button 
            size="lg"
            className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold text-xl px-12 py-7"
            onClick={() => navigate('/contact')}
          >
            Contact Us
          </Button>
        </div>
      </section>

      <Footer />

      {/* Register As Exporter Dialog */}
      <Dialog open={isRegisterOpen} onOpenChange={setIsRegisterOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0B2C4D] text-xl">Register As Exporter</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegisterSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
              <Input
                value={registerForm.company_name}
                onChange={(e) => setRegisterForm({...registerForm, company_name: e.target.value})}
                required
                placeholder="Enter company name"
                data-testid="register-company-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <Input
                value={registerForm.city}
                onChange={(e) => setRegisterForm({...registerForm, city: e.target.value})}
                required
                placeholder="Enter city"
                data-testid="register-city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number *</label>
              <Input
                value={registerForm.contact_number}
                onChange={(e) => setRegisterForm({...registerForm, contact_number: e.target.value})}
                required
                placeholder="Enter contact number"
                data-testid="register-contact"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold"
              disabled={submitting}
              data-testid="register-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Registration'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
